import React, { useState, useEffect } from "react";
import SEO from "./SEO";
import { FaBirthdayCake, FaHandshake, FaGraduationCap, FaChalkboardTeacher, FaUsers } from "react-icons/fa";
import { motion } from "framer-motion";

export default function EventHall() {
  const [currentImage, setCurrentImage] = useState({});

  const eventSections = [
    {
      id: 1,
      title: "Event & Meeting Hall",
      images: ["/hall1.jpg", "/hall2.jpg", "/hall3.jpg", "/hall4.jpg"],
      items: [
        { en: "Business Meetings", am: "የንግድ ስብሰባ", icon: <FaHandshake /> },
        { en: "Birthday Parties", am: "የልደት በዓል", icon: <FaBirthdayCake /> },
        { en: "Graduation Ceremonies", am: "ለምርቃት ፖሮግራም", icon: <FaGraduationCap /> },
        { en: "Workshops & Seminars", am: "የስራ እና የስምነት ግንዛቤ", icon: <FaChalkboardTeacher /> },
        { en: "Private Gatherings", am: "የግል ስብሰባዎች", icon: <FaUsers /> },
      ],
    },
  ];

  // Image cycling effect
  useEffect(() => {
    const interval = setInterval(() => {
      const newCurrent = {};
      eventSections.forEach((section) => {
        newCurrent[section.id] =
          currentImage[section.id] !== undefined
            ? (currentImage[section.id] + 1) % section.images.length
            : 0;
      });
      setCurrentImage(newCurrent);
    }, 3000);
    return () => clearInterval(interval);
  }, [currentImage, eventSections]);

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      <SEO
        title="Ferej Hotel - Harar, Ethiopia"
        description="Host your weddings, meetings, and events at Ferej Hotel Sol’s elegant event hall in Harar, Ethiopia."
        keywords="event hall Harar, Ferej Hotel event space, Harar wedding hall, conference hall Harar"
        url="https://ferejhotel.com/event-hall"
      />

      <section
        id="events"
        className="py-24 font-[Poppins]"
        style={{
          background:
            "linear-gradient(135deg, #fffdf5 0%, #fff5d0 50%, #ffeb99 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-yellow-800 mb-10 font-[Playfair_Display] drop-shadow-md">
            Event & Meeting Hall
          </h2>
          <p className="text-xl text-gray-700 mb-16 max-w-3xl mx-auto leading-relaxed">
            Our versatile hall is perfect for various ceremonies, business meetings, and private gatherings.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-1 gap-10">
            {eventSections.map((section, index) => (
              <motion.div
                key={section.id}
                className="bg-gradient-to-br from-[#fff8dc] via-[#ffd966] to-[#fddc70] rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-yellow-400/50 hover:shadow-lg"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={cardVariants}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Carousel Image */}
                <div className="h-60 w-full overflow-hidden relative rounded-t-3xl">
                  <img
                    src={section.images[currentImage[section.id] || 0]}
                    alt={section.title}
                    className="h-60 w-full object-cover transition-transform duration-700 hover:scale-110"
                  />
                </div>

                <div className="p-8 text-left">
                  <h3 className="text-2xl font-semibold text-yellow-900 mb-4 font-[Playfair_Display] flex items-center gap-3">
                    {section.title}
                  </h3>
                  <ul className="space-y-3 text-gray-800 text-lg">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <span className="text-yellow-700 text-xl">{item.icon}</span>
                        <span>
                          {item.en}{" "}
                          <span className="text-gray-500 italic text-sm">{item.am}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Poppins:wght@400;500;600&display=swap');
          `}
        </style>
      </section>
    </>
  );
}
